<template>

  <div>
    <Modal
      :title="$t('titles.blackout_history', {id: blackout.ID})"
      :submit-button="false"
      close-label="Close"
      fullscreen
    >

      <v-data-table
        :items="auditLogs"
        :headers="headers"
        :loading="loading"
        no-data-text="No history found for this blackout."
      >

        <template v-slot:header.timestamp="{ column }">
          {{ column.title }}
          <help-icon>In your local time zone</help-icon>
        </template>

        <template v-slot:item.timestamp="{ item }">
          {{ formatDatetime(item.timestamp, true) }}
        </template>

        <template v-slot:item.action="{ item }">
          {{ AuditLogActions[item.action] ?? item.action }}
        </template>

        <template v-slot:item.actionDetails="{ index, item }">

          <div v-if="item.actionDetails" class="my-4">
            <template            
              v-for="diff in diffActionDetails(item?.actionDetails ?? {}, index > 0 ? auditLogs[index-1]?.actionDetails ?? {} : null)"
            >
              {{ diff.label }}:
              <template v-if="'prevValue' in diff">
                {{ diff.prevValue }}
                <v-icon icon="$delta" color="grey" />
              </template>
              {{ diff.value }}
              <br>
            </template>
          </div>

        </template>

      </v-data-table>

    </Modal>
  </div>
  
</template>

<script setup lang="ts">

import { inject, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import AuditLog, { type FetchAuditLogsResponseRecord } from '../../../models/AuditLog'
import Blackout from '../../../models/Blackout'

import { AuditLogActions } from '../../../enums'
import moment from 'moment'
import NotificationService from '../../../services/NotificationService'
import PreferencesService from '../../../services/PreferencesService'
import type { Can } from '../../../main'
import type { VDataTableHeader } from '../../../types/vuetify'

interface Props {
  blackout: Blackout
}

const props = defineProps<Props>()

const { t } = useI18n()
const $can = inject('$can') as Can
const canSeeIds = $can('seeids')

const headers: VDataTableHeader[] = []

if (canSeeIds) {
  headers.push({title: t('titles.id'), key: 'ID', sortable: false})
}

headers.push(
  {title: t('titles.date_time'), key: 'timestamp', sortable: false},
  {title: t('titles.user'), key: 'username', sortable: false},
  {title: t('titles.action'), key: 'action', sortable: false},
  {title: t('titles.details'), key: 'actionDetails', sortable: false}
)

const auditLogs = ref<FetchAuditLogsResponseRecord[]>([])
const loading = ref<boolean>(false)

onMounted(() => {

  loading.value = true

  AuditLog
    .fetch({
      targetTypes: ['blackout'],
      targetIds: [props.blackout.ID],
      sortBy: 'event_dt_utc',
      desc: true,
    })
    .then(response => {
      auditLogs.value = response.records.filter(auditLog => {
        return (
          auditLog.succeeded &&
          ['create', 'update', 'delete'].includes(auditLog.action) // TODO: use actions request var instead?
        )
      })
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error('Couldn\'t load blackout history!')
    })
    .finally(() => {
      loading.value = false
    })

})

function formatDatetime(datetimeStr: string, localize: boolean = false): null | string {

  if (datetimeStr === null) {
    return null
  }

  const momentDt = moment.utc(datetimeStr)

  if (localize) {
    momentDt.tz(PreferencesService.preferences.timezone)
  }

  return momentDt.format('MM/DD/YYYY h:mm:ss a')

}

interface DetailField {
  name: string
  label: string
  formatter: (v: any) => string
}

const detailFields: DetailField[] = [
  {name: 'name', label: 'Name', formatter: (v) => v},
  {name: 'reason', label: 'Reason', formatter: (v) => v},
  {name: 'startDate', label: 'Start Date', formatter: (v) => formatDatetime(v, true)},
  {name: 'endDate', label: 'End Date', formatter: (v) => formatDatetime(v, true)},
]

interface Diff {
  label: string
  value: string
  prevValue?: string
}

function diffActionDetails(actionDetails: any, prevActionDetails: null | any = null): Diff[] {
  
  const diffs: Diff[] = []

  actionDetails = normalizeActionDetails(actionDetails)

  if (prevActionDetails) {
    prevActionDetails = normalizeActionDetails(prevActionDetails)
  }

  detailFields.forEach((field) => {

    const value = actionDetails[field.name] ?? null

    if (prevActionDetails === null) {
      diffs.push({
        label: field.label,
        value: field.formatter(value),
      })
      return
    }

    const prevValue = prevActionDetails[field.name] ?? null

    if (value !== prevValue) {
      diffs.push({
        label: field.label,
        value: field.formatter(value),
        prevValue: field.formatter(prevValue),
      })
    }

  })

  return diffs

}

function normalizeActionDetails(actionDetails: any) {

  // The action_details column for older audit log entries (before we started
  // validating the schema of that column, in March 2024) have inconsistent
  // field naming and formatting. This function accounts for that.

  const normalized = {
    id: actionDetails.id ?? actionDetails.ID ?? null,
    name: actionDetails.name ?? null,
    reason: actionDetails.reason ?? null,
    startDate: normalizeDatetimeStr(actionDetails.startDate ?? actionDetails.start_date ?? null),
    endDate: normalizeDatetimeStr(actionDetails.endDate ?? actionDetails.end_date ?? null),
    channels: actionDetails.channels ?? null,
  }

  return normalized

}

function normalizeDatetimeStr(datetimeStr = null) {

  if (!datetimeStr) {
    return null
  }

  // https://momentjs.com/docs/#/parsing/string-formats/
  const datetime = moment.utc(datetimeStr, [
    'YYYY-MM-DDTHH:mm:ss', // ISO 8601
    'YYYY-MM-DD HH:mm:ss', // non-standard variant found in some audit_log rows
  ], true)

  return datetime.isValid() ? datetime.toISOString() : null

}

</script>
